import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  useGetDocumentToFillDataMutation,
  useUploadFinanceDocumentsMutation,
} from "../../../../store/Company/companyApiSlice";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { CommonToast } from "../../../common/Toast";
import {
  checkYearValidity,
  financialYears,
  trimToFiveYears,
} from "./financialSetupFields";

/* Method 2 — hand TREVEX the statements and let the team read them. */
export const useFinancialDocumentUploads = ({ onDone } = {}) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [getDocumentToFillData] = useGetDocumentToFillDataMutation();
  const [uploadFinanceDocuments] = useUploadFinanceDocumentsMutation();

  const years = financialYears();
  const [documents, setDocuments] = useState({});
  const [uploadingYear, setUploadingYear] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState([]);
  /* The "older than five years" confirmation. */
  const [warningOpen, setWarningOpen] = useState(false);

  const load = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await getDocumentToFillData({ companyId: companyID, token });
      const seeded = {};
      (res?.data?.data || []).forEach((row) => {
        const rowYear = parseInt(String(row?.yearID || "").split("-")[0], 10);
        if (!Number.isNaN(rowYear) && row?.document) seeded[rowYear] = row.document;
      });
      setDocuments(seeded);
    } catch {
      /* Unguarded in the live version, so a failure became an unhandled
         rejection. An empty grid is the right fallback. */
      setDocuments({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID]);

  /* The array the endpoint takes: one row per year that has a document. */
  const rows = () =>
    Object.keys(documents)
      .filter((y) => documents[y])
      .map((y) => ({ year: Number(y), document: documents[y] }));

  const attach = async (year, file) => {
    if (!file) return;
    setUploadingYear(year);
    try {
      const uploaded = await uploadFileToS3(
        uploadFilePath?.auditedFinancial,
        file,
        token,
        companyID,
        "financial_document"
      );
      if (uploaded?.success === false) {
        /* uploadFileToS3 reports its own reason via a toast. */
        return;
      }
      setDocuments((prev) => ({ ...prev, [year]: uploaded?.url }));
      setError("");
      setMissing([]);
    } finally {
      setUploadingYear(null);
    }
  };

  const remove = (year) => {
    setDocuments((prev) => {
      const next = { ...prev };
      delete next[year];
      return next;
    });
  };

  const send = async (payload) => {
    setBusy(true);
    try {
      const res = await uploadFinanceDocuments({
        data: payload,
        token,
        companyId: companyID,
      });

      /* Transcribed success test and its two failure messages. */
      if (res?.data?.statusCode === 200) {
        CommonToast.success(res?.data?.message || "Documents sent for review.");
        onDone?.();
        return true;
      }

      CommonToast.error(
        res?.error?.data?.messages?.length > 0
          ? res.error.data.messages[0].message
          : "Error occured while processing your request"
      );
      return false;
    } catch (err) {
      CommonToast.error(
        err?.data?.message || err?.message || "Something went wrong"
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const current = rows();
    if (current.length === 0) {
      setError("Add at least one statement before continuing.");
      return false;
    }

    const validity = checkYearValidity(current);
    if (validity.errorMessage) {
      setError(validity.errorMessage);
      setMissing(validity.missing);
      return false;
    }
    setError("");
    setMissing([]);

    if (validity.warning) {
      /* Transcribed: accepted separately, because accepting DISCARDS rows. */
      setWarningOpen(true);
      return false;
    }

    return send(current);
  };

  /* Transcribed: accepting the warning trims everything older than five years
     and uploads what is left. */
  const acceptWarning = async () => {
    setWarningOpen(false);
    return send(trimToFiveYears(rows()));
  };

  return {
    years,
    documents,
    attach,
    remove,
    uploadingYear,
    busy,
    error,
    missing,
    warningOpen,
    closeWarning: () => setWarningOpen(false),
    acceptWarning,
    save,
  };
};

export default useFinancialDocumentUploads;
